import { UIIcon } from '../../../config/icons';
import { ABEventTagSelect } from '../../ab/select-admin';
import { c_event_tags, CEventSortOrder } from '../../mdl/c-event-tag';
import { FEventCardShape, FEventListDisplay, FPageBlock, FPageEventsQuery } from '../../mdl/f-page-block';
import { uiSetBG } from '../../ui/bg';
import { UIButton } from '../../ui/component/button';
import { UINumberInput } from '../../ui/component/input';
import { UILabeled } from '../../ui/component/labeled';
import { hLine } from '../../ui/component/line';
import { UISelect } from '../../ui/component/select';
import { UICheckbox } from '../../ui/component/toggle';
import { $el } from '../../ui/elements';
import { uiMakeValuesFromObject } from '../../ui/functions';
import { UIClassesSpec, UIValue } from '../../ui/types';
import { UIView, UIViewSpec } from '../../ui/view';
import { $on } from '../../y/events';
import { $form } from '../../y/form';
import { EDBlock, EDBlockSpec } from '../block';
import { EDBlockContainer } from '../editor';
import { fakeSegmented, fakeText } from '../elements';
import { EDI18NInput } from '../input';
import { $page_values } from './values';

interface EventQueryMode {
    name: string;
    icon: UIIcon;
}

const event_query_modes: Record<FPageEventsQuery, EventQueryMode> = {
    city: { name: 'City', icon: UIIcon.BUILDINGS },
    streams: { name: 'Streams', icon: UIIcon.VIDEO },
    tag: { name: 'Tag', icon: UIIcon.TAG },
    video_tag: { name: 'Video tag', icon: UIIcon.TAG },
}

const event_query_values: UIValue<FPageEventsQuery>[] = uiMakeValuesFromObject(event_query_modes);
const event_display_values: UIValue<FEventListDisplay>[] = [{ key: 'list', name: 'List' }, { key: 'carousel', name: 'Carousel' }, { key: 'row', name: 'Row' }];
const event_shape_values: UIValue<FEventCardShape>[] = [{ key: 'event', name: 'Event' }, { key: 'play', name: 'Play' }, { key: 'event-large', name: 'Large event' }, { key: 'play-large', name: 'Large play' }];
const event_sort_values: UIValue<CEventSortOrder>[] = [{ key: 'latest', name: 'Latest' }, { key: 'date', name: 'Date' }, { key: 'popular', name: 'Popular' }];

class FakeEventCard extends UIView {
    private _image: HTMLElement;

    constructor(classes?: UIClassesSpec) {
        const image = $el.DIV('ui-image ed-fake-image');

        super('div', ['tl-event-card', classes], [
            image,
            $el.DIV('r-texts', [
                $el.DIV('r-top', [
                    fakeText(8),
                    $el.S()
                ]),
                $el.DIV('r-title', [fakeText(14), fakeText(12)]),
                $el.DIV('r-bottom', fakeText(14))
            ])
        ]);

        this._image = image;
    }

    set icon(icon: UIIcon) {
        this._image.ui_empty($el.ICON(icon))
    }
}

class NewFakeEventCard extends UIView {
    private _image: HTMLElement;

    constructor(shape?: string) {
        const image = $el.DIV('ui-image ed-fake-image');
        super('div', ['o-event-card k-' + shape]);
        this._image = image;

        const top = $el.DIV('r-top', $el.DIV('r-genre', [
            fakeText(7),
        ]));

        const bottom = $el.DIV('r-bottom', [
            $el.DIV('r-title', fakeText(9)),
            hLine(),
            $el.DIV('r-dates', [
                $el.B(fakeText(5)),
                $el.B(fakeText(6)),
            ]),
        ])

        this.ui_add([
            top,
            image,
            bottom
        ]);
    }

    set icon(icon: UIIcon) {
        this._image.ui_empty($el.ICON(icon))
    }
}

export const ed_page_carousels_block: EDBlockSpec<FPageBlock> = {
    key: 'carousels',
    editor_block: class extends EDBlock {
        constructor(container: EDBlockContainer) {
            super($form({}), container, 'Carousels');

            const see_all = new UIButton({ title: 'See all', classes: 'ui-tint-reverse' });
            see_all.ui_hide();

            const header_row = $el.DIV('l-h r-header w-block', [
                $el.DIV('r-text', fakeText(20)),
                see_all
            ]);

            const event_cards: FakeEventCard[] = [];
            const fake_collection_card = $el.DIV('tl-image-card');
            uiSetBG(fake_collection_card, { color: '#7f7faf' });
            for(let i = 0; i < 6; i++) {
                event_cards.push(new FakeEventCard('k-event'));
            }

            const list = $el.DIV('tl-event-list k-event m-carousel', [fake_collection_card, ...event_cards]);

            this.ui_add($el.DIV('tl-events-block', [header_row, $el.DIV('tl-event-list-container w-block', list)]));
        }
    }
};

export const ed_page_events_block: EDBlockSpec<FPageBlock> = {
    key: 'events',
    editor_block: class extends EDBlock {
        constructor(container: EDBlockContainer) {
            const display = new UISelect(event_display_values, { kind: 'k-subtle' });
            const shape = new UISelect(event_shape_values, { kind: 'k-subtle' });
            const query = new UISelect(event_query_values, { kind: 'k-subtle' });
            const sort = new UISelect(event_sort_values, { kind: 'k-subtle' });
            const randomize = new UICheckbox({ title: 'Randomize on load' });
            const limit = new UINumberInput({ kind: 'k-subtle' });
            const show_header = new UICheckbox({ title: 'Show header' });
            const show_button = new UICheckbox({ title: 'Show button' });
            const color_scheme = new UISelect($page_values.color_scheme, { kind: 'k-subtle' });
            const header = new EDI18NInput({ multiline: true, styleable: true, placeholder: 'Header', classes: 'r-text p-f-events-title', fields: container.editor.fields });
            const tag = new ABEventTagSelect({ kind: 'k-subtle' });

            const properties = $el.DIV('l-form', [
                new UILabeled(display, { above: 'Display' }),
                new UILabeled(shape, { above: 'Card shape' }),
                new UILabeled(query, { above: 'Query' }),
                new UILabeled(tag, { above: 'Tag' }),
                new UILabeled(sort, { above: 'Sort' }),
                randomize,
                new UILabeled(limit, { above: 'Event limit' }),
                show_header,
                show_button,
                new UILabeled(color_scheme, { above: 'Color scheme' })
            ]);

            super($form({ display, shape, query, sort, randomize, limit, show_header, show_button, color_scheme, header, tag }), container, 'Events', properties);

            const see_all = new UIButton({ title: 'See all', classes: 'ui-tint-reverse' });
            see_all.ui_hide();

            const header_row = $el.DIV('l-h r-header w-block', [header, see_all]);
            header_row.ui_hide();

            const event_cards: FakeEventCard[] = [];
            const fake_collection_card = $el.DIV('tl-image-card');

            for(let i = 0; i < 6; i++) {
                event_cards.push(new FakeEventCard('k-event'));
            }

            const all_cards: UIViewSpec[] = [fake_collection_card, ...event_cards];
            const list = $el.DIV('tl-event-list');

            let num_cards = 3;
            let tag_has_image = false;
            let first_card = 1;

            this.ui_add($el.DIV('tl-events-block', [header_row, $el.DIV('tl-event-list-container w-block', list)]));

            $on(display, 'value', v => {
                num_cards = v == 'row' ? 3 : 6;
                list.classList.toggle('m-row', v == 'row');
                list.classList.toggle('m-carousel', v == 'carousel');
                list.ui_render(all_cards.slice(first_card, first_card + num_cards));
                limit.ui_show(undefined, v != 'row');
            });

            $on(shape, 'value', v => {
                list.classList.toggle('k-event', v == 'event');
                list.classList.toggle('k-event-large', v == 'event-large');
                list.classList.toggle('k-play', v == 'play');
                list.classList.toggle('k-play-large', v == 'play-large');
                for(const c of event_cards) {
                    c.classList.toggle('k-event', v == 'event');
                    c.classList.toggle('k-event-large', v == 'event-large');
                    c.classList.toggle('k-play', v == 'play');
                    c.classList.toggle('k-play-large', v == 'play-large');
                }
            });

            $on(query, 'value', v => {
                if(!v) return;
                const mode = event_query_modes[v];
                for(const c of event_cards) {
                    c.icon = mode.icon;
                    this.name = 'Events - ' + mode.name;
                }
                tag.ui_show(undefined, v == 'tag' || v == 'video_tag');
                first_card = ((v == 'tag' || v == 'video_tag') && tag_has_image) ? 0 : 1;

                list.ui_render(all_cards.slice(first_card, first_card + num_cards));
            });

            $on(tag, 'value', v => {
                tag_has_image = false;
                if(v) {
                    const carousel_image = c_event_tags.maybe(v)?.carousel_image;
                    if(carousel_image) {
                        uiSetBG(fake_collection_card, { media: carousel_image })
                        tag_has_image = true;
                    }
                }

                if(query.value == 'tag' || query.value == 'video_tag') {
                    first_card = tag_has_image ? 0 : 1;
                    list.ui_render(all_cards.slice(first_card, first_card + num_cards));
                }
            });

            $on(color_scheme, 'value', v => {
                list.classList.toggle('c-dark', v == 'dark');
                list.classList.toggle('c-light', v == 'light');
            });

            $on(show_header, 'value', v => {
                header_row.ui_show(undefined, !!v);
                show_button.ui_show(undefined, !!v);
            });

            $on(show_button, 'value', v => {
                see_all.ui_show(undefined, !!v);
            });
        }
    }
};

export const ed_page_featured_block: EDBlockSpec<FPageBlock> = {
    key: 'featured',
    editor_block: class extends EDBlock {
        constructor(container: EDBlockContainer) {
            super($form({}), container, 'Featured');
            this.classList.add('blk-featured', 'b', 'b-content');
            this.ui_add([new NewFakeEventCard('event'), new NewFakeEventCard('event'), new NewFakeEventCard('event')]);
        }
    },
};

export const ed_page_cityselect_block: EDBlockSpec<FPageBlock> = {
    key: 'cityselect',
    editor_block: class extends EDBlock {
        constructor(container: EDBlockContainer) {
            super($form({}), container, 'City selection');

            this.classList.add('b', 'b-content', 'blk-cityselect');
            this.ui_add(fakeSegmented('k-new'))
        }
    },
};
